import {
  Alert,
  Checkbox,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Typography,
} from "antd";
import { useMemo } from "react";
import BankCodeSelect from "../select/bankCodeSelect";
import ProductTypeSelect from "../select/productType";
import moneyFormat from "../../functions/utils/moneyFormat";
import {
  packOptions,
  payerFeePreview,
  userFeeTypeOptions,
} from "./merchantAdminConstants";

const { Text } = Typography;

/**
 * Shared merchant admin form body (create + edit). Parent owns Form + onFinish.
 */
const MerchantAdminFormFields = ({ mode = "create" }) => {
  const isCreate = mode === "create";
  const form = Form.useFormInstance();
  const userFeeType = Form.useWatch("userFeeType", form);
  const userFee = Form.useWatch("userFee", form);

  const preview = useMemo(
    () => payerFeePreview(10000, userFee, userFeeType),
    [userFee, userFeeType]
  );

  const userFeeLabel =
    userFeeType === "PERCENT"
      ? "Төлөгчийн шимтгэл (хувь)"
      : "Төлөгчийн шимтгэл (₮)";

  const requiredBank = isCreate
    ? [{ required: true, message: "Банк сонгоно уу" }]
    : [];

  const requiredAccount = isCreate
    ? [{ required: true, message: "Данс оруулна уу" }]
    : [];

  return (
    <>
      {isCreate && (
        <Alert
          type="info"
          showIcon
          className="mb-4"
          message="Юу үүсэх вэ?"
          description={
            <ul className="mb-0 pl-4 text-sm">
              <li>Мерчантын бүртгэл, нэвтрэх эрх (имэйл + санамсаргүй нууц үг)</li>
              <li>Нэхэмжлэл үүсгэх, төлбөр хүлээн авах боломж</li>
              <li>
                Төлөгчийн шимтгэл нь нэхэмжлэлийн дүн дээр нэмэгдэж төлөгдөнө
              </li>
            </ul>
          }
        />
      )}

      <Divider orientation="left" className="!mt-0">
        Байгууллага
      </Divider>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="Байгууллагын нэр"
            name="name"
            rules={[{ required: true, message: "Нэр оруулна уу" }]}
          >
            <Input placeholder="Жишээ: Ривер Плаза ХХК" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="Байгууллагын регистр" name="register">
            <Input maxLength={10} placeholder="7 орон (заавал биш)" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="Утас"
            name="phone"
            rules={[{ required: true, message: "Утас оруулна уу" }]}
          >
            <Input placeholder="99119911" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="Нэмэлт утас" name="phone2">
            <Input placeholder="Заавал биш" />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item label="Байршил" name="address">
            <Input placeholder="Хаяг (заавал биш)" />
          </Form.Item>
        </Col>
      </Row>

      <Divider orientation="left">Нэвтрэх</Divider>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="Имэйл"
            name="email"
            extra="Мерчант порталд нэвтрэх нэр"
            rules={[
              { required: true, message: "Имэйл оруулна уу" },
              { type: "email", message: "Имэйл буруу байна" },
            ]}
          >
            <Input placeholder="merchant@company.mn" />
          </Form.Item>
        </Col>
        {isCreate && (
          <Col span={24}>
            <Form.Item name="sendWelcomeEmail" valuePropName="checked">
              <Checkbox>Имэйлээр нэвтрэх нэр, нууц үг илгээх</Checkbox>
            </Form.Item>
          </Col>
        )}
      </Row>

      <Divider orientation="left">Төлбөр хүлээн авах данс</Divider>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label="Банк" name="bankCode" rules={requiredBank}>
            <BankCodeSelect />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="Дансны дугаар"
            name="accountNumber"
            extra="Өндөрлөгөөний үндсэн данс"
            rules={requiredAccount}
          >
            <Input placeholder="IBAN эсвэл дансны дугаар" />
          </Form.Item>
        </Col>
      </Row>

      <Divider orientation="left">Тохиргоо</Divider>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label="Багц" name="pack">
            <Select options={packOptions} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="Үндсэн бүтээгдэхүүний төрөл"
            name="productTypeId"
            rules={[{ required: true, message: "Сонгоно уу" }]}
          >
            <ProductTypeSelect />
          </Form.Item>
        </Col>
      </Row>

      <Divider orientation="left">Төлөгчийн шимтгэл (SmartBilling)</Divider>
      <Text type="secondary" className="mb-3 block text-sm">
        Нэхэмжлэлийн үндсэн дүн дээр нэмэгдэж төлөгч төлнө. Нэг нэхэмжлэлд нэг удаа
        тооцогдоно. Шинэ нэхэмжлэлд л хэрэгжинэ.
      </Text>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="Тооцоолох арга"
            name="userFeeType"
            rules={[{ required: true }]}
          >
            <Select options={userFeeTypeOptions} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label={userFeeLabel}
            name="userFee"
            rules={[{ required: true, message: "Дүн оруулна уу" }]}
          >
            <InputNumber
              className="w-full"
              min={0}
              step={userFeeType === "PERCENT" ? 0.1 : 1}
              max={userFeeType === "PERCENT" ? 100 : undefined}
              addonAfter={userFeeType === "PERCENT" ? "%" : "₮"}
            />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Alert
            type="success"
            showIcon={false}
            className="text-sm"
            message="Жишээ тооцоо (10,000₮ нэхэмжлэл)"
            description={
              <span>
                Нэхэмжлэлийн дүн: {moneyFormat(10000)} · Шимтгэл:{" "}
                <strong>{moneyFormat(preview.fee)}</strong> · Төлөгч төлөх нийт:{" "}
                <strong>{moneyFormat(preview.total)}</strong>
              </span>
            }
          />
        </Col>
      </Row>

      <Divider orientation="left">Мерчантын өндөрлөгөө (хувь)</Divider>
      <Text type="secondary" className="mb-3 block text-sm">
        Гүйлгээний мөрийн дүнгээс хасагдах шимтгэл. Төлөгчийн дээр нэмэх шимтгэлээс
        тусдаа.
      </Text>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="Мерчантын шимтгэл"
            name="feePercent"
            extra="Гүйлгээний дүнгийн хувь (%)"
          >
            <InputNumber
              className="w-full"
              min={0}
              max={100}
              step={0.1}
              addonAfter="%"
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="XPay хувь"
            name="spayFeePercent"
            extra="XPay-ийн хэсэг (%)"
          >
            <InputNumber
              className="w-full"
              min={0}
              max={100}
              step={0.1}
              addonAfter="%"
            />
          </Form.Item>
        </Col>
      </Row>
    </>
  );
};

export default MerchantAdminFormFields;
